import React, { useState } from 'react';
import { Check, ChevronDown, Building2, Star, Clock, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useCompanyContext } from '@/hooks/useCompanyContext';

interface CompanySwitcherProps {
  className?: string;
  compact?: boolean;
}

export const CompanySwitcher: React.FC<CompanySwitcherProps> = ({
  className,
  compact = false
}) => {
  const { selectedCompany, userCompanies } = useAuth();
  const { 
    switchCompany, 
    getFrequentCompanies, 
    isValidating,
    hasMultipleCompanies,
    totalCompanies 
  } = useCompanyContext();
  
  const [isOpen, setIsOpen] = useState(false);
  const frequentCompanies = getFrequentCompanies();

  if (!selectedCompany) {
    return (
      <div className={cn("flex items-center space-x-2 text-muted-foreground", className)}>
        <Building2 className="h-4 w-4" />
        <span className="text-sm">Sin empresa</span>
      </div>
    );
  }

  if (!hasMultipleCompanies) {
    // Mostrar solo la empresa actual de manera simplificada
    return (
      <div className={cn("flex items-center space-x-3", className)}>
        <div className="h-8 w-8 rounded-xl bg-primary/10 flex items-center justify-center">
          <Building2 className="h-4 w-4 text-primary" />
        </div>
        {!compact && (
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">
              {selectedCompany.name}
            </p>
            <p className="text-xs text-muted-foreground">
              {selectedCompany.country}
            </p>
          </div>
        )}
      </div>
    );
  }

  const handleCompanySelect = async (company: typeof selectedCompany) => {
    if (company && company.id !== selectedCompany.id) {
      setIsOpen(false);
      await switchCompany(company);
    }
  };

  return (
    <div className={cn("w-full", className)}>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              "w-full justify-between h-auto p-2.5 border-2 border-border",
              "bg-transparent hover:bg-accent/30 transition-colors",
              compact && "p-2"
            )}
            disabled={isValidating}
          >
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <div className={cn(
                "rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0",
                compact ? "h-6 w-6" : "h-8 w-8"
              )}>
                <Building2 className={cn("text-primary", compact ? "h-3 w-3" : "h-4 w-4")} />
              </div>
              {!compact && (
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate" title={selectedCompany.name}>
                    {selectedCompany.name}
                  </p>
                  <div className="flex items-center space-x-1">
                    <p className="text-xs text-muted-foreground">
                      {selectedCompany.country}
                    </p>
                    {totalCompanies > 1 && (
                      <>
                        <span className="text-xs text-muted-foreground/50">•</span>
                        <Badge variant="secondary" className="text-xs px-1.5 py-0 h-4">
                          {totalCompanies}
                        </Badge>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            <ChevronDown className={cn(
              "text-muted-foreground flex-shrink-0 transition-transform duration-200",
              compact ? "h-3 w-3" : "h-4 w-4",
              isOpen && "rotate-180"
            )} />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent 
          className="w-80 max-h-96 overflow-y-auto bg-background/95 backdrop-blur-xl border border-border/50 shadow-2xl rounded-2xl p-2"
          align="start"
          sideOffset={8}
        >
          <DropdownMenuLabel className="flex items-center justify-between px-4 py-3 mb-2">
            <span className="text-sm font-semibold text-foreground">Cambiar Empresa</span>
            <Badge variant="secondary" className="text-xs font-medium bg-accent/50 text-accent-foreground px-2 py-1 rounded-full">
              {totalCompanies} disponibles
            </Badge>
          </DropdownMenuLabel>
          
          <DropdownMenuSeparator className="mx-2 bg-border/30" />

          {/* Lógica inteligente según Apple UX/UI */}
          {totalCompanies <= 4 ? (
            // Para pocas empresas: lista simple sin secciones
            <>
              {userCompanies.map(({ company }) => (
                <DropdownMenuItem
                  key={company.id}
                  onClick={() => handleCompanySelect(company)}
                  className="flex items-center space-x-4 p-4 mx-2 my-1 cursor-pointer hover:bg-accent/40 rounded-xl transition-all duration-200 hover:scale-[1.02] group"
                >
                  <div className="flex items-center space-x-4 min-w-0 flex-1">
                    <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center flex-shrink-0 group-hover:from-primary/30 group-hover:to-primary/20 transition-all duration-300">
                      <Building2 className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        <p className="text-sm font-semibold truncate text-foreground" title={company.name}>
                          {company.name}
                        </p>
                        {company.id === selectedCompany.id && (
                          <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center">
                            <Check className="h-3 w-3 text-primary-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        <p className="text-xs text-muted-foreground">
                          {company.country}
                        </p>
                        {company.nit && (
                          <Badge variant="outline" className="text-xs px-2 py-0.5 h-5 bg-muted/30 border-border/30 rounded-md">
                            {company.nit}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </DropdownMenuItem>
              ))}
            </>
          ) : totalCompanies <= 8 ? (
            // Para empresas medianas: frecuentes + todas las empresas
            <>
              {/* Empresas frecuentes (si hay suficientes) */}
              {frequentCompanies.length > 0 && (
                <>
                  <DropdownMenuLabel className="text-xs font-medium text-muted-foreground flex items-center space-x-2 px-4 py-3 mt-2">
                    <Star className="h-4 w-4 text-yellow-500" />
                    <span>Frecuentes</span>
                  </DropdownMenuLabel>
                  
                  {frequentCompanies.slice(0, 2).map((company) => (
                    <DropdownMenuItem
                      key={`frequent-${company.id}`}
                      onClick={() => handleCompanySelect(company)}
                      className="flex items-center space-x-4 p-4 mx-2 my-1 cursor-pointer hover:bg-accent/40 rounded-xl transition-all duration-200 hover:scale-[1.02] group"
                    >
                      <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-yellow-400/20 to-yellow-600/10 flex items-center justify-center group-hover:from-yellow-400/30 group-hover:to-yellow-600/20 transition-all duration-300">
                        <Building2 className="h-5 w-5 text-yellow-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3">
                          <p className="text-sm font-semibold truncate text-foreground">{company.name}</p>
                          {company.id === selectedCompany.id && (
                            <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center">
                              <Check className="h-3 w-3 text-primary-foreground" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {company.country}
                        </p>
                      </div>
                    </DropdownMenuItem>
                  ))}
                  
                  <DropdownMenuSeparator className="mx-2 my-3 bg-border/30" />
                </>
              )}

              {/* Todas las empresas */}
              <DropdownMenuLabel className="text-xs font-medium text-muted-foreground flex items-center space-x-2 px-4 py-3 mt-2">
                <Users className="h-4 w-4" />
                <span>Otras Empresas</span>
              </DropdownMenuLabel>

              {userCompanies.map(({ company }) => (
                <DropdownMenuItem
                  key={`all-${company.id}`}
                  onClick={() => handleCompanySelect(company)}
                  className="flex items-center space-x-4 p-4 mx-2 my-1 cursor-pointer hover:bg-accent/40 rounded-xl transition-all duration-200 hover:scale-[1.02] group"
                >
                  <div className="flex items-center space-x-4 min-w-0 flex-1">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-muted/50 to-muted/30 flex items-center justify-center flex-shrink-0 group-hover:from-accent/50 group-hover:to-accent/30 transition-all duration-300">
                      <Building2 className="h-5 w-5 text-muted-foreground group-hover:text-accent-foreground transition-colors duration-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        <p className="text-sm font-semibold truncate text-foreground" title={company.name}>
                          {company.name}
                        </p>
                        {company.id === selectedCompany.id && (
                          <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center">
                            <Check className="h-3 w-3 text-primary-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        <p className="text-xs text-muted-foreground">
                          {company.country}
                        </p>
                        {company.nit && (
                          <Badge variant="outline" className="text-xs px-2 py-0.5 h-5 bg-muted/30 border-border/30 rounded-md">
                            {company.nit}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </DropdownMenuItem>
              ))}
            </>
          ) : (
            // Para muchas empresas: estructura completa
            <>
              {/* Empresas frecuentes */}
              {frequentCompanies.length > 0 && (
                <>
                  <DropdownMenuLabel className="text-xs font-medium text-muted-foreground flex items-center space-x-2 px-4 py-3">
                    <Star className="h-4 w-4 text-yellow-500" />
                    <span>Frecuentes</span>
                  </DropdownMenuLabel>
                  
                  {frequentCompanies.slice(0, 3).map((company) => (
                    <DropdownMenuItem
                      key={company.id}
                      onClick={() => handleCompanySelect(company)}
                      className="flex items-center space-x-4 p-4 mx-2 my-1 cursor-pointer hover:bg-accent/40 rounded-xl transition-all duration-200 hover:scale-[1.02] group"
                    >
                      <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-yellow-400/20 to-yellow-600/10 flex items-center justify-center group-hover:from-yellow-400/30 group-hover:to-yellow-600/20 transition-all duration-300">
                        <Building2 className="h-5 w-5 text-yellow-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3">
                          <p className="text-sm font-semibold truncate text-foreground">{company.name}</p>
                          {company.id === selectedCompany.id && (
                            <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center">
                              <Check className="h-3 w-3 text-primary-foreground" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {company.country} • {company.currency}
                        </p>
                      </div>
                    </DropdownMenuItem>
                  ))}
                  
                  <DropdownMenuSeparator className="mx-2 my-3 bg-border/30" />
                </>
              )}

              {/* Todas las empresas */}
              <DropdownMenuLabel className="text-xs font-medium text-muted-foreground flex items-center space-x-2 px-4 py-3">
                <Users className="h-4 w-4" />
                <span>Todas las Empresas</span>
              </DropdownMenuLabel>

              {userCompanies.map(({ company }) => (
                <DropdownMenuItem
                  key={company.id}
                  onClick={() => handleCompanySelect(company)}
                  className="flex items-center space-x-4 p-4 mx-2 my-1 cursor-pointer hover:bg-accent/40 rounded-xl transition-all duration-200 hover:scale-[1.02] group"
                >
                  <div className="flex items-center space-x-4 min-w-0 flex-1">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-muted/50 to-muted/30 flex items-center justify-center flex-shrink-0 group-hover:from-accent/50 group-hover:to-accent/30 transition-all duration-300">
                      <Building2 className="h-5 w-5 text-muted-foreground group-hover:text-accent-foreground transition-colors duration-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        <p className="text-sm font-semibold truncate max-w-[180px] sm:max-w-[220px] text-foreground" title={company.name}>
                          {company.name}
                        </p>
                        {company.id === selectedCompany.id && (
                          <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center">
                            <Check className="h-3 w-3 text-primary-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        <p className="text-xs text-muted-foreground truncate">
                          {company.country} • {company.currency}
                        </p>
                        {company.nit && (
                          <Badge variant="outline" className="text-xs px-2 py-0.5 h-5 bg-muted/30 border-border/30 rounded-md flex-shrink-0">
                            {company.nit}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </DropdownMenuItem>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};